export const AUTHORED_BRIDGE_COLLISION_SCHEMA_VERSION = 1 as const;

export interface AuthoredBridgeCollisionSegment {
  readonly id: string;
  readonly startLocalXM: number;
  readonly endLocalXM: number;
  readonly startSurfaceWorldM: number;
  readonly endSurfaceWorldM: number;
  readonly depthM: number;
}

export interface AuthoredBridgeCollisionSpec {
  readonly id: string;
  readonly label: string;
  /** Tile whose physics lifecycle owns this bridge's colliders. */
  readonly tileId: string;
  readonly position: Readonly<{
    x: number;
    z: number;
  }>;
  readonly rotationYRadians: number;
  readonly widthM: number;
  readonly segments: readonly AuthoredBridgeCollisionSegment[];
}

export interface AuthoredBridgeCollisionManifest {
  readonly schemaVersion:
    typeof AUTHORED_BRIDGE_COLLISION_SCHEMA_VERSION;
  readonly coordinateBasis: "local-projected-world-metres";
  readonly bridges: readonly AuthoredBridgeCollisionSpec[];
}

export interface AuthoredBridgeSurfaceSample {
  readonly bridgeId: string;
  readonly segmentId: string;
  readonly elevationM: number;
}

/**
 * Samples the authored, continuous driving surface of a bridge in world space.
 * The collision manifest is the level-aware source of truth here: terrain
 * alone cannot distinguish a deck from the river or road beneath it.
 */
export function sampleAuthoredBridgeSurface(
  bridges: readonly Readonly<AuthoredBridgeCollisionSpec>[],
  point: Readonly<{ x: number; z: number }>,
  travelYawRadians?: number,
): AuthoredBridgeSurfaceSample | null {
  let highest: AuthoredBridgeSurfaceSample | null = null;
  for (const bridge of bridges) {
    const deltaX = point.x - bridge.position.x;
    const deltaZ = point.z - bridge.position.z;
    const cosine = Math.cos(bridge.rotationYRadians);
    const sine = Math.sin(bridge.rotationYRadians);
    if (travelYawRadians !== undefined) {
      const alignment = Math.abs(
        Math.sin(travelYawRadians) * cosine -
          Math.cos(travelYawRadians) * sine,
      );
      if (alignment < Math.cos(Math.PI / 6)) continue;
    }
    const localX = deltaX * cosine - deltaZ * sine;
    const localZ = deltaX * sine + deltaZ * cosine;
    if (Math.abs(localZ) > bridge.widthM / 2 + 0.001) continue;
    const segment = bridge.segments.find(
      (candidate) =>
        localX >= candidate.startLocalXM - 0.001 &&
        localX <= candidate.endLocalXM + 0.001,
    );
    if (segment === undefined) continue;
    const alpha = Math.max(
      0,
      Math.min(
        1,
        (localX - segment.startLocalXM) /
          (segment.endLocalXM - segment.startLocalXM),
      ),
    );
    const sample = {
      bridgeId: bridge.id,
      segmentId: segment.id,
      elevationM:
        segment.startSurfaceWorldM +
        (segment.endSurfaceWorldM - segment.startSurfaceWorldM) * alpha,
    };
    if (highest === null || sample.elevationM > highest.elevationM) {
      highest = sample;
    }
  }
  return highest;
}

export function parseAuthoredBridgeCollisionManifest(
  value: unknown,
): AuthoredBridgeCollisionManifest {
  const manifest = requireRecord(value, "Bridge collision manifest");
  if (
    manifest.schemaVersion !== AUTHORED_BRIDGE_COLLISION_SCHEMA_VERSION
  ) {
    throw new Error("Bridge collision manifest schemaVersion must equal 1.");
  }
  if (manifest.coordinateBasis !== "local-projected-world-metres") {
    throw new Error(
      "Bridge collision manifest must use local-projected-world-metres.",
    );
  }
  if (!Array.isArray(manifest.bridges)) {
    throw new Error("Bridge collision manifest bridges must be an array.");
  }

  const ids = new Set<string>();
  const bridges = manifest.bridges.map((rawBridge, bridgeIndex) => {
    const path = `bridges[${bridgeIndex}]`;
    const bridge = requireRecord(rawBridge, path);
    const id = requireString(bridge.id, `${path}.id`);
    if (ids.has(id)) {
      throw new Error(`Duplicate bridge collision ID ${id}.`);
    }
    ids.add(id);
    const position = requireRecord(bridge.position, `${path}.position`);
    const widthM = requirePositiveNumber(bridge.widthM, `${path}.widthM`);
    if (!Array.isArray(bridge.segments) || bridge.segments.length === 0) {
      throw new Error(`${path}.segments must be a non-empty array.`);
    }
    const segmentIds = new Set<string>();
    const segments = bridge.segments.map((rawSegment, segmentIndex) => {
      const segmentPath = `${path}.segments[${segmentIndex}]`;
      const segment = requireRecord(rawSegment, segmentPath);
      const segmentId = requireString(segment.id, `${segmentPath}.id`);
      if (segmentIds.has(segmentId)) {
        throw new Error(
          `Duplicate bridge collision segment ID ${id}:${segmentId}.`,
        );
      }
      segmentIds.add(segmentId);
      const startLocalXM = requireFiniteNumber(
        segment.startLocalXM,
        `${segmentPath}.startLocalXM`,
      );
      const endLocalXM = requireFiniteNumber(
        segment.endLocalXM,
        `${segmentPath}.endLocalXM`,
      );
      const startSurfaceWorldM = requireFiniteNumber(
        segment.startSurfaceWorldM,
        `${segmentPath}.startSurfaceWorldM`,
      );
      const endSurfaceWorldM = requireFiniteNumber(
        segment.endSurfaceWorldM,
        `${segmentPath}.endSurfaceWorldM`,
      );
      if (endLocalXM <= startLocalXM) {
        throw new Error(`${segmentPath} must have positive local length.`);
      }
      const grade = Math.abs(
        (endSurfaceWorldM - startSurfaceWorldM) /
          (endLocalXM - startLocalXM),
      );
      const maximumGrade = segmentId.endsWith("landing") ? 0.25 : 0.080001;
      if (grade > maximumGrade) {
        throw new Error(
          `${segmentPath} exceeds its ${maximumGrade * 100}% grade limit.`,
        );
      }
      return {
        id: segmentId,
        startLocalXM,
        endLocalXM,
        startSurfaceWorldM,
        endSurfaceWorldM,
        depthM: requirePositiveNumber(
          segment.depthM,
          `${segmentPath}.depthM`,
        ),
      };
    });
    for (let segmentIndex = 1; segmentIndex < segments.length; segmentIndex += 1) {
      const previous = segments[segmentIndex - 1];
      const current = segments[segmentIndex];
      if (previous === undefined || current === undefined) {
        continue;
      }
      if (
        Math.abs(previous.endLocalXM - current.startLocalXM) > 0.001 ||
        Math.abs(
          previous.endSurfaceWorldM - current.startSurfaceWorldM,
        ) > 0.001
      ) {
        throw new Error(
          `${path}.segments must form one continuous traversal surface.`,
        );
      }
    }
    return {
      id,
      label: requireString(bridge.label, `${path}.label`),
      tileId: requireTileId(bridge.tileId, `${path}.tileId`),
      position: {
        x: requireFiniteNumber(position.x, `${path}.position.x`),
        z: requireFiniteNumber(position.z, `${path}.position.z`),
      },
      rotationYRadians: requireFiniteNumber(
        bridge.rotationYRadians,
        `${path}.rotationYRadians`,
      ),
      widthM,
      segments,
    };
  });

  return {
    schemaVersion: AUTHORED_BRIDGE_COLLISION_SCHEMA_VERSION,
    coordinateBasis: "local-projected-world-metres",
    bridges,
  };
}

export async function loadAuthoredBridgeCollisions(
  manifestUrl: string,
  fetcher: typeof fetch = fetch,
): Promise<readonly AuthoredBridgeCollisionSpec[]> {
  const response = await fetcher(manifestUrl);
  if (!response.ok) {
    throw new Error(
      `Could not load bridge collision manifest (${response.status}).`,
    );
  }
  return parseAuthoredBridgeCollisionManifest(await response.json()).bridges;
}

function requireRecord(
  value: unknown,
  path: string,
): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function requireString(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`${path} must be a non-empty string.`);
  }
  return value;
}

function requireTileId(value: unknown, path: string): string {
  const tileId = requireString(value, path);
  if (!/^-?\d+:-?\d+$/.test(tileId)) {
    throw new Error(`${path} must be a world tile ID.`);
  }
  return tileId;
}

function requireFiniteNumber(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${path} must be finite.`);
  }
  return value;
}

function requirePositiveNumber(value: unknown, path: string): number {
  const result = requireFiniteNumber(value, path);
  if (result <= 0) {
    throw new Error(`${path} must be positive.`);
  }
  return result;
}
